import { AddressForm, InfoMessage, useScopedSelector } from 'ia-react-core';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Address } from '../../../../../interfaces/Address';
import { Option } from '../../../../../interfaces/Option';
import { PersonalInformation } from '../../../../../interfaces/PersonalInformation';
import { StyledSafeHtml } from './AddressFormSection.styles';

export interface AddressFormSectionProps {
  personalInfo: PersonalInformation;
  countries: Option[];
  provinces: Option[];
}
const AddressFormSection: FC<AddressFormSectionProps> = ({ personalInfo, countries, provinces }) => {
  const { t } = useTranslation('PersonalInformationsFormPage');

  const address: Address = useScopedSelector('info.address', {} as Address);

  const defaultValues = useMemo(() => ({
    ...address,
    countryIso2: address.country,
    buildingNumber: address.civicNumber,
    poBoxNumber: address.deliveryMethod === 'PO BOX' ? address.postOfficeBox : '',
    ruralRoute: address.deliveryMethod === 'RR' ? address.postOfficeBox : '',
    place: address.externalPlace,
    subBuilding: address.appartementOrSuite,
    subBuildingType: address.appartementOrSuiteType,
    deliveryType: address.deliveryMethod,
  }), [address]);

  const provincesCountry = useMemo(() => {
    if (['CA', 'US'].includes(personalInfo?.newAddress?.countryIso2)) {
      return personalInfo?.newAddress?.countryIso2;
    }
    return null;
  }, [personalInfo?.newAddress?.countryIso2]);

  let injectScript = true;
  if (process.env.REACT_APP_PERSONAL_INFORMATIONS_USE_CSP === 'true') {
    injectScript = false;
  }

  return (
    <>
      {personalInfo?.address?.behaviours.isUpdateEnabled ?
        <AddressForm
          aria-labelledby="addressLabel"
          countries={[
            { value: 'CA', label: t('address.countryCA') },
            { value: 'US', label: t('address.countryUS') },
          ]}
          provinces={provinces}
          provincesCountry={provincesCountry}
          loadingProvinces={!provincesCountry}
          defaultCol={6}
          postCanadaApiKey={process.env.REACT_APP_PERSONAL_INFORMATIONS_PC_API_KEY}
          injectPostCanadaScript={injectScript}
          name="newAddress"
          filterSearch={false}
          subBuildingOptions={[
            { value: 'APP', label: t('address.apartment') },
            { value: 'OFF', label: t('address.office') },
            { value: 'none', label: t('address.none') },
          ]}
          deliveryOptions={[
            { value: 'PO BOX', label: t('address.postalBox') },
            { value: 'RR', label: t('address.ruralRoute') },
            { value: 'none', label: t('address.none') },
          ]}
          defaultValues={defaultValues}
          hideProvince={provincesCountry === null}
          manualEntryCountries={countries}
        /> : <InfoMessage><StyledSafeHtml>{personalInfo?.address?.behaviours.message}</StyledSafeHtml></InfoMessage>}
    </>
  );
};
export default AddressFormSection;
